<template>
  <div>
    <!-- Header Section -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="map-pin" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <h3 class="mb-0 fw-bold">Work Stations Management</h3>
        </div>
      </div>
      <div class="col-md-4">
        <div class="d-flex justify-content-end">
          <button
            v-if="profile.role !== 'user' && profile.role !== 'admin'"
            class="btn btn-primary shadow-sm"
            @click="stationModal = true; stationMode = 'add'"
          >
            <vue-feather type="plus" size="18" class="me-1"></vue-feather>
            Add Work Station
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="list" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Work Stations ({{stations.length}})
            </div>
            <div class="bg-primary-light p-2 rounded-circle">
              <vue-feather type="map-pin" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="20"></vue-feather>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th scope="col" class="fw-semibold">Code</th>
                  <th scope="col" class="fw-semibold">Name</th>
                  <th scope="col" class="fw-semibold">Description</th>
                  <th scope="col" class="fw-semibold">Created</th>
                  <th scope="col" class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody v-if="!loading">
                <tr v-for="station in stations" :key="station.id" class="station-row">
                  <td>
                    <span class="badge bg-primary-light text-primary">{{ station.code }}</span>
                  </td>
                  <td class="fw-medium">{{ station.name }}</td>
                  <td>{{ station.description || 'Not specified' }}</td>
                  <td>{{ formatDate(station.created_at) }}</td>
                  <td class="text-end">
                    <button
                      v-if="profile.role !== 'user' && profile.role !== 'admin'"
                      class="btn btn-sm btn-outline-primary"
                      @click="stationModal = true; stationMode = 'edit'; populateStationEdit(station)"
                    >
                      <vue-feather type="edit-2" size="16"></vue-feather>
                      Edit
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="loading" class="text-center py-4">
              <Spinner spinner-class="mb-1" />
            </div>
            <div v-if="!loading && stations.length === 0" class="text-center py-4">
              <div class="empty-state">
                <div class="bg-light p-4 rounded-circle mb-3 d-inline-block">
                  <vue-feather type="map-pin" size="32" stroke="#6c757d"></vue-feather>
                </div>
                <h5>No Work Stations Found</h5>
                <p class="text-muted">Start by adding your first work station</p>
                <button
                  v-if="profile.role !== 'user' && profile.role !== 'admin'"
                  class="btn btn-primary"
                  @click="stationModal = true; stationMode = 'add'"
                >
                  <vue-feather type="plus" size="16" class="me-1"></vue-feather>
                  Add Work Station
                </button>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-center mt-4">
            <button class="btn btn-outline-dark text-uppercase me-4 shadow-sm" @click="$router.go(-1)">
              <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
              Back
            </button>
          </div>
        </Card>
      </div>
    </div>

    <!-- Work Station Modal -->
    <Modal :open="stationModal" @closed="stationModal = false">
      <div class="modal-header border-0">
        <div class="d-flex align-items-center">
          <h5 class="modal-title m-0 fw-bold" v-if="stationMode === 'add'">Add Work Station</h5>
          <h5 class="modal-title m-0 fw-bold" v-else>Edit Work Station</h5>
        </div>
        <button @click="stationModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row g-3">
            <div class="col-md-12 mb-2 h5">
              <label for="code" class="form-label fw-bolder">Work Station Code</label>
              <div class="input-group">
                <input
                  type="text"
                  id="code"
                  class="form-control"
                  placeholder="Work station code"
                  v-model="station.code"
                  readonly
                >
              </div>
              <small class="text-muted fst-italic font-size-75">Code is automatically generated</small>
            </div>
            <div class="col-md-12 mb-2 h5">
              <label for="name" class="form-label fw-bolder">Work Station Name</label>
              <div class="input-group">
                <input
                  type="text"
                  id="name"
                  class="form-control"
                  placeholder="Enter work station name"
                  v-model="station.name"
                >
              </div>
            </div>
            <div class="col-md-12 mb-2 h5">
              <label for="description" class="form-label fw-bolder">Description <span class="text-muted">(optional)</span></label>
              <div class="input-group">
                <input
                  type="text"
                  id="description"
                  class="form-control"
                  placeholder="Enter work station description"
                  v-model="station.description"
                >
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer border-0 pt-0">
        <button
          type="button"
          class="btn btn-outline-secondary"
          data-dismiss="modal"
          @click="stationModal = false"
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          v-if="stationMode === 'add'"
          @click="addStation($event)"
        >
          Save Work Station
        </button>
        <button
          type="button"
          class="btn btn-primary"
          v-else
          @click="updateStation($event, station.id)"
        >
          Update Work Station
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import stationService from "@/api/stationService";
import formatDate from "../../utils/formatDate";
import {decrypt} from "@/utils/crypto";
import loadingButton from "@/utils/loadingButton";
import VueFeather from 'vue-feather';

export default {
  name: "WorkStations",
  components: { VueFeather },

  data(){
    return {
      profile: {},
      loading: true,
      stations: [],
      stationMode: 'add',
      stationModal: false,
      station: {
        code: '',
        name: '',
        description: '',
      },
      nextNo: {},
    }
  },

  methods: {
    formatDate,
    async getStations() {
      this.loading = true
      try {
        this.stations = await stationService.getStations()
        this.nextNo = await stationService.getStationNextNo()
        this.station.code = this.nextNo.next
      } catch (error) {
        console.error("Error fetching work stations:", error)
      } finally {
        this.loading = false
      }
    },

    populateStationEdit(station) {
      this.station = { ...station }
    },

    async addStation(e) {
      if (!this.station.name) {
        return
      }

      loadingButton(e)
      try {
        let response = await stationService.createStation(this.station)
        if (response) {
          this.stationModal = false
          await this.getStations()
        }
      } catch (error) {
        console.error("Error adding work station:", error)
      } finally {
        loadingButton(e, false)
      }
    },

    async updateStation(e, id) {
      if (!this.station.name) {
        return
      }

      loadingButton(e)
      try {
        let response = await stationService.updateStation(this.station, id)
        if (response) {
          this.stationModal = false
          await this.getStations()
        }
      } catch (error) {
        console.error("Error updating work station:", error)
      } finally {
        loadingButton(e, false)
      }
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    await this.getStations()
    if(this.$route.query.add)
      this.stationModal = true
  },

  watch: {
    stationModal: function () {
      if (!this.stationModal) {
        this.station = {
          code: this.nextNo.next,
          name: '',
          description: '',
        }
      }
    },
  }
}
</script>

<style scoped>
.bg-primary-light {
  background-color: rgba(101, 42, 163, 0.1);
}

.text-primary {
  color: #652aa3 !important;
}

.station-row {
  transition: all 0.2s ease;
}

.station-row:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.empty-state {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.font-size-125 {
  font-size: 1.25rem;
}

.font-size-75 {
  font-size: 0.75rem;
}

.color-gray-600 {
  color: #6c757d;
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.btn {
  transition: all 0.2s ease;
}

.btn:hover {
  transform: translateY(-2px);
}

.input-group {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  border-radius: 0.25rem;
}

.input-group:focus-within {
  box-shadow: 0 2px 10px rgba(101, 42, 163, 0.15);
}
</style>
